/**
 * AI SERVICE HOOK
 * React hook for interacting with CodeMind AI services
 */

import { useState, useCallback } from 'react';

interface ChatMessage {
  message: string;
  sessionId: string;
  context?: any;
}

interface ChatResponse {
  response: string;
  suggestions: string[];
  codeBlocks: Array<{
    language: string;
    code: string;
  }>;
  processingTime: number;
}

export function useAIService() {
  const [isLoading, setIsLoading] = useState(false);
  const [isAIReady, setIsAIReady] = useState(true);

  const sendChatMessage = useCallback(async (message: ChatMessage): Promise<ChatResponse> => {
    setIsLoading(true);
    
    try {
      const response = await fetch('https://codemind-ai.codeworker.workers.dev/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      });

      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'AI service unavailable');
      }

      return {
        response: data.response,
        suggestions: data.suggestions || [],
        codeBlocks: data.codeBlocks || [],
        processingTime: data.processingTime || 0,
      };

    } catch (error) {
      console.error('AI service error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const analyzeCode = useCallback(async (code: string, language: string) => {
    setIsLoading(true);
    
    try {
      const response = await fetch('https://codemind-ai.codeworker.workers.dev/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          language,
          analysisType: 'comprehensive',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Code analysis failed');
      }

      return data.analysis;

    } catch (error) {
      console.error('Code analysis error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    sendChatMessage,
    analyzeCode,
    isLoading,
    isAIReady,
  };
}